import { filterNotEmpty } from '../../listHelpers';
import {
  DAY_OF_MONTH_FORMAT,
  DAY_OF_WEEK_FORMAT,
  MONTH_FORMAT,
  TIME_FORMAT,
  TIMEZONE_FORMAT,
  YEAR_FORMAT
} from './DATE_FORMATS';
import type { Comma, DatePart, Format, FormatOptions, Separator, SortableDatePart } from './formatTypes';

export function buildDateFormatString(options: FormatOptions): string {
  const startFormats = getStartFormats(options);
  const sortedFormats = toSortedFormats(options);
  const endFormats = getEndFormats(options);

  return withFinalSpaces(
    withCommaBetweenNumbers([
      ...startFormats,
      ...(showSeparators(options) ? insertBetween(sortedFormats, options.separator) : sortedFormats),
      ...endFormats
    ]),
    options.separator
  );
}

function withFinalSpaces(formats: (Format | Separator | Comma)[], separator: Separator): string {
  return formats
    .flatMap((e, i) => {
      return [
        e,
        isSeparator(e, separator) || isSeparator(formats[i + 1] ?? '', separator) || isComma(formats[i + 1] ?? '')
          ? ''
          : ' '
      ];
    })
    .slice(0, -1)
    .join('');
}

function withCommaBetweenNumbers(formats: (Format | Separator)[]): (Format | Separator | Comma)[] {
  const alwaysShowCommasFor = new Set([DAY_OF_WEEK_FORMAT.LONG, DAY_OF_WEEK_FORMAT.SHORT]);
  const conditionallyShowCommasFor = new Map<Format, Format[]>([
    [MONTH_FORMAT.DIGIT, [YEAR_FORMAT.FULL, YEAR_FORMAT.TWO_DIGITS]],
    [MONTH_FORMAT.TWO_DIGIT, [YEAR_FORMAT.FULL, YEAR_FORMAT.TWO_DIGITS]],
    [DAY_OF_MONTH_FORMAT.TWO_DIGIT, [YEAR_FORMAT.FULL, YEAR_FORMAT.TWO_DIGITS]],
    [DAY_OF_MONTH_FORMAT.DIGIT, [YEAR_FORMAT.FULL, YEAR_FORMAT.TWO_DIGITS]]
  ]);

  return formats.reduce<ReturnType<typeof withCommaBetweenNumbers>>((acc, current, index) => {
    if (alwaysShowCommasFor.has(current as any) && index < formats.length - 1) {
      return [...acc, current, ','];
    }
    const rules = conditionallyShowCommasFor.get(current as any);
    if (rules && rules.includes(formats[index + 1] as any)) {
      return [...acc, current, ','];
    }
    return [...acc, current];
  }, []);
}

function isSeparator(item: string, separator: Separator): item is Separator {
  return item === separator;
}

function isComma(item: string): item is Comma {
  return item === ',';
}

function insertBetween<T, Y>(array: T[], insertItem: Y): (T | Y)[] {
  return array.flatMap((e) => [e, insertItem]).slice(0, -1);
}

function getStartFormats({ display, format }: FormatOptions): DAY_OF_WEEK_FORMAT[] {
  const parts = ['DAY_OF_WEEK'] satisfies DatePart[];
  return filterNotEmpty(
    parts.map((part) => {
      return display.includes(part) ? format[part] : undefined;
    })
  );
}

function getEndFormats({ display, format }: FormatOptions): (TIME_FORMAT | TIMEZONE_FORMAT)[] {
  const parts = ['TIME', 'TIMEZONE'] satisfies DatePart[];
  return filterNotEmpty(
    parts.map((part) => {
      return display.includes(part) ? format[part] : undefined;
    })
  );
}

function toSortedFormats(options: FormatOptions): (YEAR_FORMAT | MONTH_FORMAT | DAY_OF_MONTH_FORMAT)[] {
  let result: SortableDatePart[];
  switch (options.dateOrder) {
    case 'YEAR_MONTH_DAY':
      result = getDateParts(options, 'YEAR', 'MONTH', 'DAY_OF_MONTH');
      break;
    case 'MONTH_DAY_YEAR':
      result = getDateParts(options, 'MONTH', 'DAY_OF_MONTH', 'YEAR');
      break;
    case 'DAY_MONTH_YEAR':
      result = getDateParts(options, 'DAY_OF_MONTH', 'MONTH', 'YEAR');
      break;
  }
  return result.map((it) => options.format[it]);
}

function getDateParts(
  { display }: FormatOptions,
  part1: SortableDatePart,
  part2: SortableDatePart,
  part3: SortableDatePart
): SortableDatePart[] {
  return filterNotEmpty([
    display.find((part) => isSortableDatePart(part, part1)),
    display.find((part) => isSortableDatePart(part, part2)),
    display.find((part) => isSortableDatePart(part, part3))
  ]);
}

function isSortableDatePart(part: DatePart, target: SortableDatePart): part is SortableDatePart {
  return part === target;
}

function showSeparators({ display, format }: FormatOptions): boolean {
  const month = display.find((part) => part === 'MONTH');
  return !!month && [MONTH_FORMAT.DIGIT, MONTH_FORMAT.TWO_DIGIT].includes(format.MONTH as MONTH_FORMAT);
}
